/**
 * Available Backend Brand Identifiers:
 * - "LifeEssentialsClub" (support@lifeessentials.club)
 * - "TomMillerServices" (contact@tommillerservices.com)
 * - "ByronBayToken" (support@byronbaytoken.com)
 * - "TheConnectionCode" (support@theconnectioncode.com)
 * 
 * Each brand has its own email templates, logos, and sender configurations
 * on the backend. The brandIdentifier must match exactly.
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;                // Main site/brand name displayed in headers and titles
  tagline: string;                 // Short brand slogan or value proposition
  description: string;             // Longer brand description for about sections and meta tags

  // Technical Configuration
  tenantId: string;                // Unique MongoDB tenant ID for data isolation
  apiUrl?: string;                 // Optional override for GraphQL API endpoint
  brandIdentifier: string;         // Backend brand identifier for email templates and notifications

  // Logo and Images
  logo: {
    src: string;                  // Logo image URL or emoji/icon
    alt: string;                  // Alt text for accessibility
    favicon: string;              // Browser favicon path
  };

  // Contact Information
  contact: {
    businessType: string;         // Type of business (e.g., "Business & Blockchain Solutions")
    industry: string;             // Industry category (e.g., "Technology")
    email?: string;               // Primary contact email
    phone?: string;               // Primary contact phone
    address?: string;             // Business physical address
    contactName?: string;         // Primary contact person name
    businessName?: string;        // Legal business name
  };

  // Color Palette
  colors: {
    // Additional accent colors (used throughout the app)
    accentBlue?: string;          // Secondary blue accent color
    primaryBlue?: string;         // Main blue for CTAs and highlights
    primaryBlueHover?: string;    // Hover state for primary blue elements
    starYellow?: string;          // Color for star/favorite icons
    starYellowHover?: string;     // Hover state for star icons
    successGreen?: string;        // Success indicators and checkmarks
    purpleAccent?: string;        // Purple accent for special highlights
    
    // Email module specific colors
    taskCardBg?: string;          // Background for task cards in task lists
    emailPreviewBg?: string;      // Background for email content preview areas
    darkCardBg?: string;          // Dark background for cards in dark theme
    darkBorderSubtle?: string;    // Subtle borders in dark theme
    
    // Highlight colors (for text highlighting and badges)
    highlightYellow?: string;     // Yellow highlight background
    highlightGreen?: string;      // Green highlight background
    highlightBlue?: string;       // Blue highlight background
    highlightPurple?: string;     // Purple highlight background
    highlightRed?: string;        // Red highlight background
    
    badges?: any;                 // Badge color schemes with bg, color, and border
    
    // Primary brand colors
    primary: string;              // Main brand color (buttons, links, primary actions)
    primaryHover: string;         // Hover state for primary color elements
    secondary: string;            // Secondary brand color (secondary actions)
    secondaryHover: string;       // Hover state for secondary color elements

    // Accent colors
    accent: string;               // Accent color for highlights and special elements
    accentLight: string;          // Lighter variant of accent color

    // Background colors
    background: {
      main: string;               // Main page background color
      light: string;              // Light background for sections
      card: string;               // Card/panel background color
      overlay: string;            // Modal/overlay background with transparency
      surface: string;            // Surface color for elevated elements
      darkSurface?: string;       // Dark theme surface color
      cardGradient?: string;      // Gradient background for cards in dark theme
      darkOverlay?: string;       // Dark overlay with transparency
      taskCard?: string;          // Task item background in task lists
      previewBg?: string;         // Email/content preview area background
    };

    // Status colors
    status: {
      success: string;            // Success states, confirmations, completed items
      warning: string;            // Warning states, caution indicators
      error: string;              // Error states, failed operations, deletions
      info: string;               // Informational states, hints, tips
      draft?: string;             // Draft email/document status
      sent?: string;              // Sent email status
      failed?: string;            // Failed email/operation status
      scheduled?: string;         // Scheduled email/task status
      inbound?: string;           // Inbound/received email status
    };

    // Text colors
    text: {
      primary: string;            // Primary text color (headings, important text)
      secondary: string;          // Secondary text color (body text)
      muted: string;              // Muted text (hints, placeholders, less important)
      inverse: string;            // Inverse text (white text on dark backgrounds)
      primaryDark?: string;       // Primary text for dark theme
      secondaryDark?: string;     // Secondary text for dark theme
      mutedDark?: string;         // Muted text for dark theme
    };

    // Border colors
    border: {
      light: string;              // Light borders (subtle separators)
      medium: string;             // Medium borders (standard borders)
      dark: string;               // Dark borders (emphasized borders)
      darkCard?: string;          // Card borders in dark theme
    };
  };

  // Typography
  fonts: {
    heading: string;              // Font family for headings (h1-h6)
    body: string;                 // Font family for body text and UI elements
  };

  // Component styling
  components: {
    button: {
      primaryBg: string;          // Primary button background color
      primaryHover: string;       // Primary button hover background
      secondaryBg: string;        // Secondary button background color
      secondaryHover: string;     // Secondary button hover background
      whiteBg?: string;           // White button background (for dark backgrounds)
      whiteText?: string;         // White button text color
      whiteHover?: string;        // White button hover background
      blueBg?: string;            // Blue button background (email module)
      blueHover?: string;         // Blue button hover background
    };
    tooltip?: {
      bg: string;                 // Tooltip background color
      color: string;              // Tooltip text color
      errorBg?: string;           // Error tooltip background
      darkBg?: string;            // Dark theme tooltip background
    };
    menu?: {
      bg: string;                 // Dropdown menu background
      itemBg: string;             // Menu item background
      itemColor: string;          // Menu item text color
      itemHoverBg: string;        // Menu item hover background
      itemHoverColor: string;     // Menu item hover text color
      borderColor: string;        // Menu border color
    };
    card: {
      bg: string;                 // Card background color
      border: string;             // Card border color
      shadow: string;             // Card shadow (elevation)
      darkShadow?: string;        // Card shadow in dark theme
      backdropFilter?: string;    // Backdrop filter for glass effect
      hoverShadow?: string;       // Card shadow on hover
    };
    navbar: {
      bg: string;                 // Navigation bar background
      text: string;               // Navigation bar text color
      shadow: string;             // Navigation bar shadow
    };
    footer: {
      bg: string;                 // Footer background color
      text: string;               // Footer text color
      linkColor: string;          // Footer link color
      linkHover: string;          // Footer link hover color
    };
    form: {
      bg: string;                 // Form background color
      fieldBg: string;            // Input field background
      fieldBorder: string;        // Input field border
      fieldBorderFocus: string;   // Input field border when focused
      fieldShadow: string;        // Input field shadow
      fieldShadowFocus: string;   // Input field shadow when focused
      labelColor: string;         // Form label text color
      placeholderColor: string;   // Input placeholder text color
    };
  };

  // Business specific
  membership: {
    totalLegacyMemberships: number;  // Total number of legacy members
    memberBenefits: {
      discount: string;               // Member discount description
      features: string[];             // List of member benefits/features
    };
  };
}

// One Solar New Zealand Configuration
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "One Solar New Zealand",
  tagline: "New Zealand's Most Trusted Solar Installer",
  description: "Hamilton solar panel installations. Obligation free solar quote. Leading local solar installers in Hamilton, Auckland, Wellington, and throughout New Zealand.",

  // Technical Configuration
  tenantId: "68ba9db45b43ae1223fb4513",
  brandIdentifier: "OneSolarNewZealand",

  // Logo and Images
  logo: {
    src: "/assets/onesolarimages/One-logo.png",
    alt: "One Solar New Zealand Logo",
    favicon: "/assets/onesolarimages/One-logo.png"
  },

  // Contact Information
  contact: {
    businessType: "Solar Energy Solutions",
    industry: "Renewable Energy",
    email: "hello@onesolarnz.co.nz",
    phone: "+64 7 123 4567",
    address: "21c Somerset Street, Frankton, Hamilton, New Zealand",
    contactName: "One Solar Team",
    businessName: "One Solar New Zealand"
  },

  // Color Palette - One Solar Brand Colors
  colors: {
    // Primary brand colors (One Solar blue)
    primary: "#121c47",        // One Solar dark blue
    primaryHover: "#0a1230",   // Darker blue on hover
    secondary: "#00a651",      // One Solar green
    secondaryHover: "#008541", // Darker green

    // Accent colors
    accent: "#121c47",
    accentLight: "#4a5478",    // Lighter blue

    // Background colors (Dark gradient theme)
    background: {
      main: "#08090a",         // Very dark, almost black
      light: "#FAFAFA",        // Off-white
      card: "#FFFFFF",         // White cards
      overlay: "rgba(0, 122, 255, 0.05)", // Light blue overlay
      surface: "#F5F5F7",      // Mac surface gray
      // New dark theme additions
      darkSurface: "#08090a",
      cardGradient: "linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)",
      darkOverlay: "rgba(0, 0, 0, 0.2)",
      taskCard: "rgba(45, 45, 55, 0.3)",     // Dark gray for task items
      previewBg: "rgba(45, 45, 55, 0.3)"      // Email preview background
    },

    // Status colors (Mac system colors)
    status: {
      success: "#30D158",      // Mac green
      warning: "#FF9F0A",      // Mac orange
      error: "#FF3B30",        // Mac red
      info: "#007AFF",         // Mac blue
      draft: "#6B7280",        // Gray for draft status
      sent: "#22C55E",         // Green for sent
      failed: "#EF4444",       // Red for failed
      scheduled: "#3B82F6",    // Blue for scheduled
      inbound: "#8B5CF6"       // Purple for inbound
    },

    // Text colors (Mac-like typography)
    text: {
      primary: "#1D1D1F",      // Mac primary text
      secondary: "#6E6E73",    // Mac secondary text
      muted: "#8E8E93",        // Mac tertiary text
      inverse: "#FFFFFF",      // White text
      // Dark theme text colors
      primaryDark: "#FFFFFF",
      secondaryDark: "rgba(255, 255, 255, 0.7)",
      mutedDark: "rgba(255, 255, 255, 0.5)"
    },

    // Border colors (Subtle Mac borders)
    border: {
      light: "rgba(0, 0, 0, 0.05)",   // Very light border
      medium: "rgba(0, 0, 0, 0.1)",   // Light border
      dark: "rgba(0, 0, 0, 0.2)",     // Medium border
      // Dark theme borders
      darkCard: "rgba(255, 255, 255, 0.1)"
    },
    
    // Additional colors
    accentBlue: "#369eff",
    primaryBlue: "#3B82F6",
    primaryBlueHover: "#2563EB",
    starYellow: "#FBBF24",
    starYellowHover: "#F59E0B",
    successGreen: "#22C55E",
    purpleAccent: "#A855F7",
    
    // Email module specific colors
    taskCardBg: "rgba(45, 45, 55, 0.3)",
    emailPreviewBg: "rgba(45, 45, 55, 0.3)",
    darkCardBg: "rgba(20, 20, 20, 0.9)",
    darkBorderSubtle: "rgba(255, 255, 255, 0.05)",
    highlightYellow: "#FEF3C7",
    highlightGreen: "#D1FAE5",
    highlightBlue: "#DBEAFE",
    highlightPurple: "#E9D5FF",
    highlightRed: "#FEE2E2",
    
    // Badge colors with transparency
    badges: {
      blue: {
        bg: "rgba(59, 130, 246, 0.2)",
        color: "#3B82F6",
        border: "rgba(59, 130, 246, 0.3)"
      },
      green: {
        bg: "rgba(34, 197, 94, 0.2)",
        color: "#22C55E",
        border: "rgba(34, 197, 94, 0.3)"
      },
      purple: {
        bg: "rgba(168, 85, 247, 0.2)",
        color: "#A855F7",
        border: "rgba(168, 85, 247, 0.3)"
      },
      yellow: {
        bg: "rgba(251, 191, 36, 0.2)",
        color: "#FBBF24",
        border: "rgba(251, 191, 36, 0.3)"
      },
      gray: {
        bg: "rgba(107, 114, 128, 0.2)",
        color: "#6B7280",
        border: "rgba(107, 114, 128, 0.3)"
      },
      orange: {
        bg: "rgba(251, 146, 60, 0.2)",
        color: "#FB923C",
        border: "rgba(251, 146, 60, 0.3)"
      },
      teal: {
        bg: "rgba(20, 184, 166, 0.2)",
        color: "#14B8A6",
        border: "rgba(20, 184, 166, 0.3)"
      },
      cyan: {
        bg: "rgba(6, 182, 212, 0.2)",
        color: "#06B6D4",
        border: "rgba(6, 182, 212, 0.3)"
      }
    }
  },

  // Typography (Thunder Compute uses Geist font)
  fonts: {
    heading: "Geist, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
    body: "Geist, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif"
  },

  // Component styling
  components: {
    button: {
      primaryBg: "#007AFF",
      primaryHover: "#0051D0",
      secondaryBg: "rgba(0, 122, 255, 0.1)",
      secondaryHover: "rgba(0, 122, 255, 0.2)",
      // Dark theme button styles (actually for light buttons on dark backgrounds)
      whiteBg: "#FFFFFF",
      whiteText: "#000000",
      whiteHover: "#F5F5F5",
      // Email module specific buttons
      blueBg: "#3B82F6",
      blueHover: "#2563EB"
    },
    tooltip: {
      bg: "#3B82F6",
      color: "#FFFFFF",
      errorBg: "#EF4444",
      darkBg: "#1a1a2e"
    },
    menu: {
      bg: "rgba(60, 60, 60, 0.98)",           // Silver-ish dark background
      itemBg: "transparent",                   // Transparent item background
      itemColor: "#E5E5E5",                   // Light silver text
      itemHoverBg: "rgba(192, 192, 192, 0.2)", // Silver hover background
      itemHoverColor: "#FFFFFF",               // White text on hover
      borderColor: "rgba(192, 192, 192, 0.3)"  // Silver border
    },
    card: {
      bg: "#FFFFFF",
      border: "rgba(0, 0, 0, 0.06)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      // Dark theme card styles
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#08090a",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
    },
    footer: {
      bg: "#121c47",
      text: "rgba(255, 255, 255, 0.7)",
      linkColor: "#369eff",
      linkHover: "#5AC8FA"
    },
    form: {
      bg: "#FFFFFF",
      fieldBg: "#FFFFFF",
      fieldBorder: "rgba(0, 0, 0, 0.1)",
      fieldBorderFocus: "#007AFF",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      fieldShadowFocus: "0 0 0 3px rgba(0, 122, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)",
      labelColor: "#1D1D1F",
      placeholderColor: "#8E8E93"
    }
  },

  // Business specific
  membership: {
    totalLegacyMemberships: 500,
    memberBenefits: {
      discount: "Professional rates",
      features: [
        "Blockchain Integration",
        "Custom Development",
        "Security Consulting",
        "24/7 Support"
      ]
    }
  },
};

// Helper functions for easy access
export const getColor = (path: string): string => {
  const keys = path.split(".");
  
  // Special handling for component paths
  if (keys[0] === "components") {
    let value: any = brandConfig;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        console.warn(`Color path "${path}" not found in brand config`);
        return "#007AFF"; // Fallback to primary
      }
    }
    return value;
  }
  
  // Default handling for colors object
  let value: any = brandConfig.colors;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Color path "${path}" not found in brand config`);
      return "#007AFF"; // Fallback to primary
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string): string => {
  const componentConfig = (brandConfig.components as any)[component];
  if (!componentConfig) {
    console.warn(`Component "${component}" not found in brand config`);
    return "#007AFF";
  }

  const value = componentConfig[property];
  if (!value) {
    console.warn(`Property "${property}" not found for component "${component}"`);
    return "#007AFF";
  }

  return value;
};


// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};
      
      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};
      
      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};
      
      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};
      
      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};
      
      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig; 